import { useState } from "react";
import { StrategyHoldings } from "./StrategyHoldings";
import { StrategyHistory } from "./StrategyHistory";
import type { StrategyCard as StrategyCardData } from "../../services/portfolioService";

interface StrategyCardProps {
  strategy: StrategyCardData;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onPreview?: () => void;
  onExecute?: () => void;
  onDeposit?: () => void;
  onWithdraw?: () => void;
  onLiquidate?: () => void;
  onCancel?: () => void;
  onPerformance?: () => void;
}

export function StrategyCard({
  strategy,
  isExpanded = false,
  onToggleExpand,
  onPreview,
  onExecute,
  onDeposit,
  onWithdraw,
  onLiquidate,
  onCancel,
  onPerformance,
}: StrategyCardProps) {
  const [activeTab, setActiveTab] = useState<"holdings" | "history">("holdings");

  const formatCurrency = (value: number | string | undefined | null) => {
    if (value === undefined || value === null) return "$0.00";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const formatPercent = (value: number | string | undefined | null) => {
    if (value === undefined || value === null) return "+0.00%";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "+0.00%";
    const sign = num >= 0 ? "+" : "";
    return `${sign}${num.toFixed(2)}%`;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const todayChange = strategy.today_change;
  const todayChangePct = strategy.today_change_pct;
  
  // Parse string values to numbers for comparisons
  const todayChangeNum = parseFloat(todayChange) || 0;
  const cumulativeReturnPctNum = parseFloat(strategy.cumulative_return_pct) || 0;

  return (
    <div className="glass-card overflow-hidden transition-all hover:border-surface-600/50">
      {/* Main Row */}
      <div
        className="p-4 sm:p-5 cursor-pointer"
        onClick={onToggleExpand}
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {/* Strategy Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span
                className={`w-2 h-2 rounded-full ${
                  strategy.is_active ? "bg-green-400" : "bg-surface-500"
                }`}
              />
              <h3 className="text-base sm:text-lg font-semibold text-surface-100 truncate">
                {strategy.strategy_name}
              </h3>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-surface-400">
              <span>{formatDate(strategy.started_at)}</span>
              <span className="text-surface-600">•</span>
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                {strategy.brokerage_account_name}
              </span>
              <span
                className={`px-1.5 py-0.5 rounded text-xs ${
                  strategy.brokerage_account_type === "live"
                    ? "bg-green-500/20 text-green-400"
                    : "bg-amber-500/20 text-amber-400"
                }`}
              >
                {strategy.brokerage_account_type}
              </span>
              {/* Threshold badge */}
              {strategy.threshold_pct && (
                <span className="px-1.5 py-0.5 rounded text-xs bg-primary-500/20 text-primary-400">
                  {parseFloat(strategy.threshold_pct) === 0 
                    ? 'Daily' 
                    : `${(parseFloat(strategy.threshold_pct) * 100).toFixed(0)}%`}
                </span>
              )}
              {/* New/Live status badge */}
              <span
                className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                  strategy.has_executed
                    ? "bg-blue-500/20 text-blue-400"
                    : "bg-purple-500/20 text-purple-400"
                }`}
              >
                {strategy.has_executed ? "Live" : "New"}
              </span>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {onPerformance && (
              <button
                onClick={onPerformance}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors flex items-center gap-1.5"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Performance
              </button>
            )}
            <button
              onClick={onPreview}
              className="px-3 py-1.5 text-xs font-medium rounded-lg text-surface-300 hover:text-surface-100 hover:bg-surface-700/50 transition-colors"
            >
              Dry Run
            </button>
            {strategy.has_executed && onDeposit && (
              <button
                onClick={onDeposit}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors"
              >
                Add
              </button>
            )}
            {strategy.has_executed && onWithdraw && (
              <button
                onClick={onWithdraw}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-colors"
              >
                Withdraw
              </button>
            )}
            {/* Run Now: always visible but styled differently for new vs existing */}
            <button
              onClick={onExecute}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                strategy.has_executed
                  ? "bg-primary-500/20 text-primary-400 hover:bg-primary-500/30"
                  : "bg-primary-500 text-white hover:bg-primary-600"
              }`}
            >
              {strategy.has_executed ? "Run Now" : "Execute"}
            </button>
            {/* Cancel: only for strategies that have NEVER been executed */}
            {!strategy.has_executed && onCancel && (
              <button
                onClick={onCancel}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-colors"
                title="Cancel investment (remove from portfolio)"
              >
                Cancel
              </button>
            )}
            {/* Liquidate: only for strategies that HAVE been executed */}
            {strategy.has_executed && onLiquidate && (
              <button
                onClick={onLiquidate}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                title="Liquidate investment (sell all positions)"
              >
                Liquidate
              </button>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-surface-700/50">
          <div>
            <div className="text-xs text-surface-400 mb-1">Today's Change</div>
            <div className={`text-sm font-medium ${todayChangeNum >= 0 ? "text-green-400" : "text-red-400"}`}>
              {todayChangeNum >= 0 ? "↑" : "↓"} {formatPercent(todayChangePct)} ({formatCurrency(todayChange)})
            </div>
          </div>
          <div>
            <div className="text-xs text-surface-400 mb-1">Net Deposits</div>
            <div className="text-sm font-medium text-surface-200">
              {formatCurrency(strategy.net_deposits)}
            </div>
            {strategy.pending_delta !== 0 && (
              <div className={`text-xs mt-1 ${
                strategy.pending_delta > 0 ? "text-green-400" : "text-amber-400"
              }`}>
                {strategy.pending_delta > 0 ? "+" : ""}{formatCurrency(strategy.pending_delta)} queued
              </div>
            )}
          </div>
          <div>
            <div className="text-xs text-surface-400 mb-1">Current Value</div>
            <div className="text-sm font-medium text-surface-200">
              {formatCurrency(strategy.current_value)}
            </div>
          </div>
          <div>
            <div className="text-xs text-surface-400 mb-1">Return</div>
            {strategy.has_executed ? (
              <div className={`text-sm font-medium ${cumulativeReturnPctNum >= 0 ? "text-green-400" : "text-red-400"}`}>
                {formatPercent(strategy.cumulative_return_pct)}
              </div>
            ) : (
              <div className="text-sm font-medium text-surface-500">
                —
              </div>
            )}
          </div>
        </div>

        {/* Expand indicator */}
        <div className="flex flex-col items-center mt-3">
          <span className="text-xs text-surface-400 mb-1">Details</span>
          <svg
            className={`w-5 h-5 text-surface-500 transition-transform ${isExpanded ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-surface-700/50">
          {/* Tabs */}
          <div className="flex border-b border-surface-700/50">
            <button
              onClick={() => setActiveTab("holdings")}
              className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === "holdings"
                  ? "text-primary-400 border-primary-400"
                  : "text-surface-400 border-transparent hover:text-surface-200"
              }`}
            >
              Holdings
            </button>
            <button
              onClick={() => setActiveTab("history")}
              className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === "history"
                  ? "text-primary-400 border-primary-400"
                  : "text-surface-400 border-transparent hover:text-surface-200"
              }`}
            >
              History
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-4 sm:p-5">
            {activeTab === "holdings" ? (
              <StrategyHoldings 
                investmentId={strategy.id} 
                strategyId={strategy.strategy_id}
                currentValue={parseFloat(strategy.current_value) || 100000}
              />
            ) : (
              <StrategyHistory investmentId={strategy.id} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
